import Save from '@mui/icons-material/Save'
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ClinicHeader from 'src/clinic/components/ClinicHeader'
import { GenericHeaderBackButton } from 'src/consumer/components/UserActivityPage/MyPlanComponents'
import { ROLE_TYPES } from 'src/consumer/constants'
import { getDataWithoutParams, putDataWithoutParams } from 'src/consumer/services/featureServices'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import {
  getDataMapFromCommonResponse,
  getMessageMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSPDataWithoutParams, putSPDataWithoutParams } from 'src/supportPortal/services/services'
import { normalizeClinicScheduleForm, validateClinicFunction } from './clinic-schedule-utils'
import ClinicScheduleComponent from './components/ClinicScheduleComponent'

const SUPPORT_CLINIC_DATA_STORAGE_KEY = 'supportClinicData'

function readSupportClinicData(clinicLocationId) {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SUPPORT_CLINIC_DATA_STORAGE_KEY) || 'null')
    if (!parsed) {
      return null
    }
    const parsedId = parsed?.id || parsed?.clinicLocationId
    return !clinicLocationId || parsedId === clinicLocationId ? parsed : null
  } catch (error) {
    return null
  }
}

export default function ClinicSchedulePage({ portal = 'clinic-owner' }) {
  const { clinicBrandId, clinicId, clinicLocationId } = useParams()
  const { chosenRoleFlow, ownerData, refreshOwnerAssociatedClinicsData } =
    useContext(ClinicDataContext) || {}
  const isSupportPortal = portal === 'support'
  const effectiveClinicLocationId = isSupportPortal ? clinicLocationId : clinicId

  const thisClinicData = useMemo(() => {
    if (isSupportPortal) {
      return readSupportClinicData(effectiveClinicLocationId) || {}
    }
    return {
      ...(ownerData?.associatedClinicsData?.find(
        (item) => item?.id === effectiveClinicLocationId,
      ) || {}),
      apiParam: 'clinic-profile',
    }
  }, [effectiveClinicLocationId, isSupportPortal, ownerData?.associatedClinicsData])
  const thisClinicDataRef = useRef(thisClinicData)

  const [formData, setFormData] = useState({})
  const [changesMade, setChangesMade] = useState(false)
  const [loadingSchedules, setLoadingSchedules] = useState(false)
  const [savingData, setSavingData] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [invalidSchedules, setInvalidSchedules] = useState([])

  function setData(prev) {
    setChangesMade(true)
    setFormData(prev)
  }

  const pageTitle = `Clinic Schedule: ${formData?.title || formData?.clinicLocationName || formData?.name || thisClinicData?.title || thisClinicData?.clinicLocationName || effectiveClinicLocationId || ''}`
  const scheduleCount = Array.isArray(formData?.schedules) ? formData.schedules.length : 0
  const availableDayCount = useMemo(() => {
    return (formData?.schedules || []).reduce((count, schedule) => {
      return (
        count +
        Object.values(schedule?.weeklyHourMap || {}).filter((daySchedule) => daySchedule?.available)
          .length
      )
    }, 0)
  }, [formData?.schedules])

  const buildFormData = useCallback(
    (baseClinicData = {}, schedulesFromApi) => {
      return normalizeClinicScheduleForm({
        ...baseClinicData,
        id: effectiveClinicLocationId || baseClinicData?.id,
        clinicBrandId: baseClinicData?.clinicBrandId || clinicBrandId,
        apiParam: 'clinic-profile',
        schedules: Array.isArray(schedulesFromApi) ? schedulesFromApi : baseClinicData?.schedules,
      })
    },
    [clinicBrandId, effectiveClinicLocationId],
  )

  const loadSchedules = useCallback(async () => {
    const baseClinicData = thisClinicDataRef.current || {}
    if (!effectiveClinicLocationId) {
      setLoadError('Clinic location is missing from the URL.')
      setFormData(buildFormData(baseClinicData))
      return
    }

    setLoadingSchedules(true)
    setLoadError('')
    try {
      const endpoint = isSupportPortal
        ? `/api/secure/support/clinic-schedules/${effectiveClinicLocationId}`
        : `/api/secure/clinic-schedules/${effectiveClinicLocationId}`
      const response = isSupportPortal
        ? await getSPDataWithoutParams(endpoint)
        : await getDataWithoutParams(endpoint)

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const dataMap = getDataMapFromCommonResponse(response) || {}
        const nextFormData = buildFormData(
          {
            ...baseClinicData,
            clinicBrandId: dataMap?.clinicBrandId || baseClinicData?.clinicBrandId || clinicBrandId,
          },
          dataMap?.schedules,
        )
        setFormData(nextFormData)
        setInvalidSchedules([])
        setChangesMade(false)
      } else {
        const message =
          getMessageMapFromCommonResponse(response) ||
          response?.response?.data?.message ||
          'Unable to load this clinic schedule.'
        setLoadError(message)
        setFormData(buildFormData(baseClinicData))
      }
    } catch (error) {
      setLoadError('Unable to load this clinic schedule. Please try again.')
      setFormData(buildFormData(baseClinicData))
    } finally {
      setLoadingSchedules(false)
    }
  }, [buildFormData, clinicBrandId, effectiveClinicLocationId, isSupportPortal])

  async function handleSubmit(event) {
    event.preventDefault()
    setSavingData(true)
    const form = event.currentTarget
    if (form.checkValidity() === false) {
      event.stopPropagation()
    } else {
      const normalizedFormData = normalizeClinicScheduleForm(formData)
      var scheduleValidObjects = validateClinicFunction(normalizedFormData)
      setInvalidSchedules(scheduleValidObjects.schedulesValidationArray)
      if (scheduleValidObjects.schedules) {
        const payload = {
          clinicBrandId: normalizedFormData?.clinicBrandId || clinicBrandId,
          clinicLocationId: effectiveClinicLocationId,
          schedules: normalizedFormData.schedules,
        }
        const endpoint = isSupportPortal
          ? `/api/secure/support/clinic-schedules/${effectiveClinicLocationId}`
          : `/api/secure/clinic-schedules/${effectiveClinicLocationId}`
        const response = isSupportPortal
          ? await putSPDataWithoutParams(endpoint, payload)
          : await putDataWithoutParams(endpoint, payload)

        if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
          const dataMap = getDataMapFromCommonResponse(response) || {}
          const savedFormData = buildFormData(
            normalizedFormData,
            dataMap?.schedules || payload.schedules,
          )
          setFormData(savedFormData)
          setChangesMade(false)
          setInvalidSchedules([])
          if (isSupportPortal) {
            sessionStorage.setItem(
              SUPPORT_CLINIC_DATA_STORAGE_KEY,
              JSON.stringify({ ...thisClinicData, ...savedFormData }),
            )
          } else if (refreshOwnerAssociatedClinicsData) {
            refreshOwnerAssociatedClinicsData()
          }
          toast.success('Schedule saved successfully.')
          window.scrollTo(0, 0)
        } else {
          toast.error(
            getMessageMapFromCommonResponse(response) ||
              response?.response?.data?.message ||
              'Something went wrong while saving this schedule.',
          )
        }
      }
    }
    setSavingData(false)
  }

  useEffect(() => {
    thisClinicDataRef.current = thisClinicData
  }, [thisClinicData])

  useEffect(() => {
    loadSchedules()
  }, [loadSchedules])

  if (!isSupportPortal && chosenRoleFlow !== ROLE_TYPES.roleClinicOwner) {
    return null
  }

  return (
    <>
      {isSupportPortal ? (
        <SupportHeader
          heading={pageTitle}
          subHeading="Set the weekly working hours used for clinic bookings."
          backPath="/support-home/clinics-hospitals/clinics"
        />
      ) : (
        <>
          <ClinicHeader heading={pageTitle || 'Clinic Schedule'} />
          <GenericHeaderBackButton
            clickfunction={() => window.history.back()}
            styles={{ margin: '20px' }}
          />
        </>
      )}

      <Box sx={{ maxWidth: 980, mx: 'auto', px: { xs: 2, md: 3 }, py: 2 }}>
        <Stack spacing={2}>
          {loadError ? (
            <Alert severity="warning" action={<Button onClick={loadSchedules}>Retry</Button>}>
              {loadError}
            </Alert>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'background.paper',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Weekly availability
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  These hours are used as the default booking window for this location.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={`${scheduleCount} schedule${scheduleCount === 1 ? '' : 's'}`}
                />
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`${availableDayCount} open day${availableDayCount === 1 ? '' : 's'}`}
                />
                {changesMade ? <Chip size="small" color="warning" label="Unsaved changes" /> : null}
              </Stack>
            </Stack>

            {loadingSchedules ? (
              <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <ClinicScheduleComponent
                  teacherData={formData}
                  setTeacherData={setData}
                  invalidSchedules={invalidSchedules}
                />
                <Box
                  sx={{
                    position: 'sticky',
                    bottom: 16,
                    mt: 3,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.94)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 2,
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    startIcon={
                      savingData ? <CircularProgress color="inherit" size={18} /> : <Save />
                    }
                    disabled={savingData || !effectiveClinicLocationId}
                  >
                    {savingData ? 'Saving...' : 'Save schedule'}
                  </Button>
                </Box>
              </form>
            )}
          </Paper>
        </Stack>
      </Box>
    </>
  )
}
